import { ApolloQueryResult, useMutation, useQuery } from '@apollo/client';
import { GET_FORUM_TOPIC, LIST_FORUM_TOPICS } from '../../services/graphql/queries/admin';
import { CREATE_FORUM_TOPIC, CREATE_FORUM_TOPIC_COMMENT, UPDATE_FORUM_TOPIC_COMMENT } from '../../services/graphql/mutations/common';
import { useCallback } from 'react';
import { ForumTopicType } from '../../types';

type ForumFilter = {
  tags?: string | null
  authors?: string[] | null
  pilotGroupIds?: string[] | null
  categories?: string[] | null
}

interface UseGetForumTopicReturn {
  data: ForumTopicType | undefined;
  loading: boolean;
  refetch: (variables?: { id: string }) => Promise<ApolloQueryResult<{ getForumTopic: ForumTopicType }>>;
}

export default function useListAllForumTopics(page?: number, perPage?: number, filter?: ForumFilter) {
  const { data, loading, refetch } = useQuery(LIST_FORUM_TOPICS, {
    variables: {
      page,
      perPage,
      // ...(filter?.author || filter?.tags ? {
      //   filter: {
      //     ...(filter?.author && { name: filter.author }),
      //     ...(filter?.tags && { status: filter.tags }),
      //   }
      // } : {})
      filter: {
        ...(filter?.authors && { authors: filter.authors }),
        ...(filter?.tags && { tags: filter.tags }),
        ...(filter?.pilotGroupIds && { pilotGroupIds: filter.pilotGroupIds }),
        ...(filter?.categories && { categories: filter.categories }),
      }
    }
  });

  return {
    data: data?.listForumTopics?.data,
    page: data?.listForumTopics?.page,
    perPage: data?.listForumTopics?.perPage,
    total: data?.listForumTopics?.total,
    loading,
    refetch
  };
};

export const useGetForumTopic = (id: string): UseGetForumTopicReturn => {
  const { data, loading, refetch } = useQuery(GET_FORUM_TOPIC, {
    variables: {
      id
    }
  });

  return {
    data: data?.getForumTopic,
    loading,
    refetch
  };
}

export const useCreateForumTopic = () => {
  const [createForumTopic, { loading }] = useMutation(CREATE_FORUM_TOPIC);

  const createData = useCallback(async (values: any) => {
    try {
      const { data } = await createForumTopic({
        variables: {
          input: {
            params: {
              title: values.title,
              body: values.body,
              tags: values.tags,
              coverImage: values.coverImage,
              category: values.category,
              communitySubgroupId: values.communitySubgroupId,
              attachments: values.attachments
            },
          },
        }
      });
      return data.createForumTopic;
    } catch (error) {
      console.log('Error in createForumTopic: ', error);
    }
  }, [createForumTopic]);

  return {
    create: createData,
    loading
  };
};

export const useCreateForumTopicComment = () => {
  const [createForumTopicComment, { loading }] = useMutation(CREATE_FORUM_TOPIC_COMMENT);

  const createData = useCallback(async (values: any) => {
    try {
      const { data } = await createForumTopicComment({
        variables: {
          input: {
            params: {
              body: values.body,
              forumTopicId: values.forumTopicId
            },
          },
        }
      });
      return data.createForumTopicComment;
    } catch (error) {
      console.log('Error in createForumTopicComment: ', error);
    }
  }, [createForumTopicComment]);

  return {
    create: createData,
    loading
  };
};

export const useUpdateForumTopicComment = () => {
  const [updateForumTopicComment, { loading }] = useMutation(UPDATE_FORUM_TOPIC_COMMENT);

  const updateData = useCallback(async (values: any) => {
    try {
      const { data } = await updateForumTopicComment({
        variables: {
          input: {
            params: {
              body: values.body,
              id: values.forumTopicId
            },
          },
        }
      });
      return data.updateForumTopicComment;
    } catch (error) {
      console.log('Error in updateForumTopicComment: ', error);
    }
  }, [updateForumTopicComment]);

  return {
    update: updateData,
    loading
  };
};